import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { match } from 'ts-pattern';
import { Creator } from '~/clientApi/creator.model';
import { FEATURE_FLAGS } from '~/constants';
import { useListMediaKits } from '~/hooks/api/media-kits/list';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useDefaultSelectedCreator } from '~/screens/SearchContent/components/ContentSearchFilter/CreatorSelector/useDefaultSelectedCreator';

import { AllShareableEmptyList } from './AllShareableEmptyList';
import { CustomShareablesHeader } from './CustomShareablesHeader';
import { MediaKitsGrid } from './MediaKitsGrid';
import { MediaKitsLoadingGrid } from './MediaKitsLoadingGrid';
import { SelectTalentModal } from './components/SelectTalentModal';

interface MediaKitsPageQuery {
  creator?: string;
}

export const AllShareables = () => {
  const router = useRouter();
  const { creator } = router.query as MediaKitsPageQuery;

  const mediaKits = useListMediaKits(creator);
  const { data: selectedCreator } = useDefaultSelectedCreator(creator);
  const isStickyHeaderEnabled =
    useFeatureFlag(FEATURE_FLAGS.foamMediaKitsStickyHeader) === 'enabled';

  const handleCreatorChange = useCallback(
    (creatorId?: string) => {
      router.push(
        {
          pathname: router.pathname,
          query: creatorId ? { creator: creatorId } : {},
        },
        undefined,
        { shallow: true },
      );
    },
    [router],
  );

  // Extract media kits list for empty state check
  const mediaKitsList = useMemo(() => {
    return mediaKits.data?.pages.map((page) => page.results).flat() || [];
  }, [mediaKits.data]);

  const header = (
    <CustomShareablesHeader
      creatorId={creator}
      onCreatorChange={handleCreatorChange}
    />
  );

  return (
    <>
      {isStickyHeaderEnabled ? (
        <div className="top-0 z-10 -mt-6 bg-neutral-0 pt-6 after:pointer-events-none after:absolute after:inset-x-0 after:bottom-[-8px] after:z-[11] after:h-[8px] after:bg-gradient-to-b after:from-[var(--wb-color-neutral-0)] after:to-[var(--wb-color-white-alpha-0)] after:content-[''] mac:sticky">
          {header}
        </div>
      ) : (
        header
      )}
      {match(mediaKits.status)
        .with('loading', () => <MediaKitsLoadingGrid />)
        .with('success', () =>
          mediaKitsList.length > 0 ? (
            <MediaKitsGrid mediaKits={mediaKits} />
          ) : (
            <AllShareableEmptyList creator={selectedCreator as Creator} />
          ),
        )
        .with('error', () => (
          //Todo: We need to implement error page here
          <AllShareableEmptyList />
        ))
        .exhaustive()}
      <SelectTalentModal />
    </>
  );
};
